import { type Action, createActionsHook, createStateHook, createStore } from 'react-sweet-state';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

export const havePulsesEnded = () => {
	return !!localStorage.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_CROSS_SELL_DISCOVERABILITY_PULSES_HAVE_ENDED,
	);
};

export const endPulses = () => {
	return localStorage.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_LOOM_CROSS_SELL_DISCOVERABILITY_PULSES_HAVE_ENDED,
		true,
	);
};

type State = {
	activeMessageId: string | null;
	entryPointIds: string[];
	clickTrackingHandler: (() => void) | null;
	dismissHandler: (() => void | Promise<void>) | null;
	wasDismissedByUser: boolean;
};

const initialState: State = {
	activeMessageId: null,
	entryPointIds: [],
	clickTrackingHandler: null,
	dismissHandler: null,
	wasDismissedByUser: false,
};

const actions = {
	start:
		(
			entryPointId: string,
			messageId: string,
			handlers?: {
				onDismiss?: () => void | Promise<void>;
				onClick?: () => void;
			},
		): Action<State> =>
		({ getState, setState }) => {
			const state = getState();
			const nextState: State =
				state.activeMessageId !== messageId
					? {
							activeMessageId: messageId,
							entryPointIds: [entryPointId],
							clickTrackingHandler: handlers?.onClick ?? null,
							dismissHandler: handlers?.onDismiss ?? null,
							wasDismissedByUser: false,
						}
					: {
							activeMessageId: messageId,
							entryPointIds: state.entryPointIds.includes(entryPointId)
								? state.entryPointIds
								: [...state.entryPointIds, entryPointId],
							clickTrackingHandler: handlers?.onClick ?? state.clickTrackingHandler,
							dismissHandler: handlers?.onDismiss ?? state.dismissHandler,
							wasDismissedByUser: false,
						};

			const hasEntryPointListChanged = nextState.entryPointIds !== state.entryPointIds;
			const hasClickHandlerChanged = nextState.clickTrackingHandler !== state.clickTrackingHandler;
			const hasDismissHandlerChanged = nextState.dismissHandler !== state.dismissHandler;
			const hasMessageChanged = nextState.activeMessageId !== state.activeMessageId;
			const hasDismissalReset = state.wasDismissedByUser;

			if (
				!hasEntryPointListChanged &&
				!hasClickHandlerChanged &&
				!hasDismissHandlerChanged &&
				!hasMessageChanged &&
				!hasDismissalReset
			) {
				return;
			}

			setState(nextState);
		},
	stopPostOffice:
		(): Action<State> =>
		({ getState, setState }) => {
			const state = getState();
			state.clickTrackingHandler?.();
			void state.dismissHandler?.();
			if (!havePulsesEnded()) {
				endPulses();
			}
			if (
				state.activeMessageId === null &&
				state.entryPointIds.length === 0 &&
				state.dismissHandler === null &&
				state.clickTrackingHandler === null &&
				state.wasDismissedByUser
			) {
				return;
			}
			setState({
				activeMessageId: null,
				entryPointIds: [],
				clickTrackingHandler: null,
				dismissHandler: null,
				wasDismissedByUser: true,
			});
		},
};

const Store = createStore<State, typeof actions>({
	initialState,
	actions,
	name: 'LoomEntryPointsDiscoverabilityPulse',
});

export const useIsPulseActive = createStateHook(Store, {
	selector: (state, entryPointId: string) =>
		state.entryPointIds.includes(entryPointId) && !state.wasDismissedByUser,
});

export const useDiscoverabilityPulseActions = createActionsHook(Store);
